import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const HowItWorksSection = () => {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      icon: "UserPlus",
      title: "Build Relationship Profiles",
      description: "Create detailed profiles for your loved ones, including their interests, personality traits, and gift history.",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop",
      features: ["Interest mapping", "Personality insights", "Gift history tracking", "Preference learning"]
    },
    {
      icon: "Brain",
      title: "AI-Powered Recommendations",
      description: "Our intelligent system analyzes relationships, occasions, and preferences to suggest perfect gift matches.",
      image: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?w=600&h=400&fit=crop",
      features: ["Smart matching", "Occasion awareness", "Budget optimization", "Cultural sensitivity"]
    },
    {
      icon: "Users",
      title: "Community Insights",
      description: "Learn from successful gift-givers in our community and discover what works for similar relationships.",
      image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600&h=400&fit=crop",
      features: ["Success stories", "Community reviews", "Trending gifts", "Expert advice"]
    },
    {
      icon: "Gift",
      title: "Perfect Gift Delivery",
      description: "Complete your purchase with confidence, knowing you've found a gift that will create meaningful moments.",
      image: "https://images.pexels.com/photos/1303081/pexels-photo-1303081.jpeg?w=600&h=400&fit=crop",
      features: ["Secure checkout", "Gift wrapping", "Personal messages", "Delivery tracking"]
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-warm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl lg:text-5xl font-bold text-accent-800 mb-6">
            How Giftsy Modern Works
          </h2>
          <p className="text-lg lg:text-xl text-accent-600 max-w-3xl mx-auto leading-relaxed">
            Our intelligent platform combines AI technology with human insights to make thoughtful gift-giving effortless and meaningful.
          </p>
        </div>

        {/* Step Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {steps.map((step, index) => (
            <button
              key={index}
              onClick={() => setActiveStep(index)}
              className={`flex items-center space-x-3 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                activeStep === index
                  ? 'bg-primary text-white shadow-primary'
                  : 'bg-white text-accent-600 hover:bg-primary-50 hover:text-primary-700 shadow-sm'
              }`}
            >
              <Icon 
                name={step.icon} 
                size={20} 
                className={activeStep === index ? 'text-white' : 'text-accent-500'}
              />
              <span className="hidden sm:inline">Step {index + 1}</span>
              <span className="sm:hidden">{index + 1}</span>
            </button>
          ))}
        </div>

        {/* Active Step Content */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-0">
            <div className="p-8 lg:p-12">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-xl flex items-center justify-center">
                  <Icon name={steps[activeStep].icon} size={28} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-primary-600 mb-1">
                    Step {activeStep + 1} of {steps.length}
                  </p>
                  <h3 className="font-heading text-2xl lg:text-3xl font-bold text-accent-800">
                    {steps[activeStep].title}
                  </h3>
                </div>
              </div>
              
              <p className="text-lg text-accent-600 mb-8 leading-relaxed">
                {steps[activeStep].description}
              </p>
              
              <div className="space-y-4">
                <h4 className="font-heading text-lg font-bold text-accent-800 mb-4">
                  Key Features:
                </h4>
                {steps[activeStep].features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-success-100 rounded-full flex items-center justify-center">
                      <Icon name="Check" size={14} className="text-success-600" />
                    </div>
                    <span className="text-accent-600">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <Image
                src={steps[activeStep].image}
                alt={`${steps[activeStep].title} illustration`}
                className="w-full h-64 lg:h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-black/20"></div>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center mt-8">
          <div className="flex space-x-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === activeStep ? 'bg-primary' : 'bg-accent-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;